import { ReactNode, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Heading, Paragraph } from '@/core/components/Typography';
import { TextBodyBold, TextBodySmallBold } from '@/core/components/Typography/Typography.styles';
import type { ParagraphProps } from '@/core/components/Typography/Typography.types';

import {
  InlineCode,
  ListItemContent,
  MarkdownList,
  MarkdownListItem,
  MarkdownOrderedList,
  MarkdownRoot,
  MarkdownTable,
  MarkdownTableWrapper,
} from './MarkdownText.styles';
import { MarkdownTextProps } from './MarkDown.types';

const REMARK_PLUGINS = [remarkGfm];

const normalizeMarkdownHeadings = (raw: string): string =>
  raw.replace(/([^\n\r])## /g, '$1\n\n## ');

const normalizeStrikethrough = (raw: string): string =>
  raw.replace(/<s>\s*<\/s>/gi, '').replace(/<s>([\s\S]*?)<\/s>/gi, '~~$1~~');

const normalizeHtmlAnchors = (raw: string): string =>
  raw.replace(/<a\s[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, '[$2]($1)');

const markdownComponents = (paragraphType: ParagraphProps['type']) => ({
  p: (props: { children?: ReactNode }) => (
    <Paragraph type={paragraphType}>{props.children}</Paragraph>
  ),
  h1: (props: { children?: ReactNode }) => <Heading type="h1">{props.children}</Heading>,
  h2: (props: { children?: ReactNode }) => <Heading type="h2">{props.children}</Heading>,
  h3: (props: { children?: ReactNode }) => <Heading type="h3">{props.children}</Heading>,
  strong: (props: { children?: ReactNode }) =>
    paragraphType === 'normal' ? (
      <TextBodyBold as="span">{props.children}</TextBodyBold>
    ) : (
      <TextBodySmallBold as="span">{props.children}</TextBodySmallBold>
    ),
  em: (props: { children?: ReactNode }) => <em>{props.children}</em>,
  a: (props: { href?: string; children?: ReactNode }) => (
    <a
      href={props.href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {props.children}
    </a>
  ),
  del: (props: { children?: ReactNode }) => <s>{props.children}</s>,
  ul: (props: { children?: ReactNode }) => <MarkdownList>{props.children}</MarkdownList>,
  ol: (props: { children?: ReactNode }) => (
    <MarkdownOrderedList>{props.children}</MarkdownOrderedList>
  ),
  li: (props: { children?: ReactNode }) => (
    <MarkdownListItem>
      <ListItemContent $variant={paragraphType}>{props.children}</ListItemContent>
    </MarkdownListItem>
  ),
  code: (props: { children?: ReactNode }) => <InlineCode>{props.children}</InlineCode>,
  table: (props: { children?: ReactNode }) => (
    <MarkdownTableWrapper>
      <MarkdownTable>{props.children}</MarkdownTable>
    </MarkdownTableWrapper>
  ),
});

export const MarkdownText = ({ text, paragraphType = 'small-normal' }: MarkdownTextProps) => {
  const normalized = useMemo(
    () => normalizeHtmlAnchors(normalizeStrikethrough(normalizeMarkdownHeadings(text))),
    [text]
  );
  const components = useMemo(() => markdownComponents(paragraphType), [paragraphType]);

  return (
    <MarkdownRoot>
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        components={components}
      >
        {normalized}
      </ReactMarkdown>
    </MarkdownRoot>
  );
};
